'use client';

import { useEffect, useState } from 'react';
import {
  FolderPlus, Plus, Trash2, Check,
  Users, CalendarClock, Hash, FileText, MapPin, Building2, StickyNote, ShieldAlert, Flag, UserCheck,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import { avisoError, avisoExito, avisoInfo } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import DefensaCard from '@/components/decisiones/DefensaCard';
import {
  getCatalogos, radicar, labelTipoImplicado, labelTipoIngreso, labelModalidad,
  reglaPrescripcion, buscarExpedientesPorDocumento,
  getExpediente, editarEtapa2,
  type Catalogos, type ExpedienteDetalle,
  type InvestigadoInput, type RadicarInput, type BusquedaPorDocumento,
} from '@/lib/expedientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PreRadicacionForm from '@/components/PreRadicacionForm';
import { radicarSolicitud, obtenerSolicitud, listarAsignadas, labelCanal, type Solicitud } from '@/lib/preRadicacion';
import { reenviarDecision } from '@/lib/decisionFlujo';
import { usuarioActual } from '@/lib/instruccion';
import { esFillerEtapa2 } from '@/lib/roles';
import { DEPARTAMENTOS, municipiosDe } from '@/lib/colombia';
import { cn } from '@/lib/utils';

// Valores por defecto de los enums (fallback si /catalogos aún no responde).
const TIPO_INGRESO_DEF = ['DE_OFICIO', 'QUEJOSO', 'INFORME', 'ANONIMO'];
const MODALIDAD_DEF = ['INSTANTANEA', 'PERMANENTE', 'CONTINUADA', 'OMISIVA'];
const TIPO_DOC_DEF = ['CC', 'CE', 'TI', 'NIT', 'PASAPORTE'];
const TIPO_IMPL_DEF = ['DETERMINADO', 'POR_ESTABLECER'];
const CALIDAD_DEF: string[] = [];

// Sentinela para "sin selección" en Selects opcionales (shadcn no admite value="").
const NINGUNO = '__NINGUNO__';

// Investigado del formulario (todos los campos como texto controlado).
interface InvForm {
  tipo: 'DETERMINADO' | 'POR_ESTABLECER';
  nombre: string; tipoDocumento: string; numeroDocumento: string;
  cargo: string; entidadNombre: string; dependencia: string; calidad: string;
  // Aviso de duplicado: expedientes previos con este número de documento.
  dup?: BusquedaPorDocumento | null;
  dupCargando?: boolean;
}
function invVacio(): InvForm {
  return { tipo: 'DETERMINADO', nombre: '', tipoDocumento: 'CC', numeroDocumento: '', cargo: '', entidadNombre: '', dependencia: '', calidad: '' };
}

const hoy = () => new Date().toISOString().slice(0, 10);

// Fecha (YYYY-MM-DD) + 5 años, para la fecha base de prescripción (Art. 33).
// Usa UTC para normalizar el 29-feb igual que el backend (setFullYear).
function masCincoAnios(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]) + 5, Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10);
}

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

// Cada ítem es una cuadrícula clicable de la Etapa 2 (estilo maqueta): al dar clic se abre
// su panel para diligenciarlo y se «chulea» cuando queda lleno. Se omiten los datos que ya
// captura la Etapa 1 · Pre-radicación (abogado/reparto, radicado de origen, PDF de la queja).
const CAMPOS: Array<{ id: string; label: string; icon: LucideIcon; requerido?: boolean; ayuda?: string }> = [
  { id: 'origen', label: '¿Quién interpone la notificación disciplinaria?', icon: Users, requerido: true },
  { id: 'fechaQueja', label: 'Fecha de la notificación disciplinaria', icon: CalendarClock, requerido: true },
  { id: 'radicadoManual', label: 'Radicado manual de la notificación disciplinaria', icon: Hash, ayuda: 'Se trae de la pre-radicación; edítelo solo si aplica.' },
  { id: 'investigados', label: 'Investigados / implicados', icon: Users, requerido: true },
  { id: 'fechaHechos', label: 'Fecha de los hechos', icon: CalendarClock },
  { id: 'descripcion', label: 'Descripción de los hechos', icon: FileText, requerido: true },
  { id: 'lugar', label: 'Lugar de los hechos', icon: MapPin },
  { id: 'dependencia', label: 'Dependencia involucrada', icon: Building2 },
  { id: 'observaciones', label: 'Observaciones', icon: StickyNote },
  { id: 'apoderado', label: 'Apoderado', icon: UserCheck, ayuda: '¿Hay apoderado? Si es «Sí», diligencie sus datos.' },
  { id: 'prescripcion', label: 'Prescripción (Art. 33)', icon: ShieldAlert, ayuda: 'Modalidad + fecha de los hechos; la base se calcula sola (+5 años).' },
  { id: 'prioridad', label: 'Prioridad', icon: Flag },
];

export default function LibroRadicador({
  onVolverExpediente,
}: { onVolverExpediente?: (id: string) => void } = {}) {
  const [cat, setCat] = useState<Catalogos | null>(null);
  // Modo edición: el contratista/tecnólogo corrige la Etapa 2 de un expediente
  // devuelto por el especialista (DEVUELTA_ETAPA2). Se entra desde "Mis expedientes"
  // o el detalle; se sale guardando (PATCH) y volviendo al expediente.
  const [modoEdicion, setModoEdicion] = useState(false);
  const [edicionExpId, setEdicionExpId] = useState<string | null>(null);
  // Estado de la decisión del expediente en edición: solo en DEVUELTA_ETAPA2 tiene
  // sentido «Guardar y reenviar» (en PENDIENTE_CONTRATISTA se propone desde el panel).
  const [edicionEstado, setEdicionEstado] = useState<string | null>(null);
  // Expediente en edición (para generar desde aquí el auto de reconocimiento de personería).
  const [edicionExp, setEdicionExp] = useState<ExpedienteDetalle | null>(null);

  // Cabecera de la queja.
  const [tipoIngreso, setTipoIngreso] = useState('DE_OFICIO');
  const [fechaQueja, setFechaQueja] = useState(hoy());
  const [radicadoManual, setRadicadoManual] = useState('');
  const [modalidad, setModalidad] = useState('');
  const [prioridad, setPrioridad] = useState('');

  // Quejoso (solo si tipoIngreso === 'QUEJOSO').
  const [qNombre, setQNombre] = useState('');
  const [qTipoDoc, setQTipoDoc] = useState('CC');
  const [qNumDoc, setQNumDoc] = useState('');
  const [qDireccion, setQDireccion] = useState('');
  const [qMunicipio, setQMunicipio] = useState('');
  const [qDepartamento, setQDepartamento] = useState('');
  const [qTelefono, setQTelefono] = useState('');
  const [qCorreo, setQCorreo] = useState('');
  // Solo ANÓNIMO: de dónde proviene la queja (texto corto).
  const [qProcedencia, setQProcedencia] = useState('');
  // INFORME: secretaría / dependencia / entidad que lo remite y cargo del funcionario.
  // El quejoso no tiene columnas propias para ellos: viajan en «procedencia»
  // (ver componerInforme / leerInforme).
  const [qEntidad, setQEntidad] = useState('');
  const [qCargo, setQCargo] = useState('');

  // Hechos. La fecha de los hechos puede ser REAL o TENTATIVA (excluyentes: solo una).
  const [hFechaReal, setHFechaReal] = useState('');
  const [hFechaTentativa, setHFechaTentativa] = useState('');
  const [hLugar, setHLugar] = useState('');
  const [hDescripcion, setHDescripcion] = useState('');
  const [hDependencia, setHDependencia] = useState('');
  const [hObservaciones, setHObservaciones] = useState('');

  // Apoderado (opcional). '' = sin responder, 'SI'/'NO'.
  const [tieneApoderado, setTieneApoderado] = useState('');
  const [apNombre, setApNombre] = useState('');
  const [apDocumento, setApDocumento] = useState('');
  const [apTarjeta, setApTarjeta] = useState('');
  const [apCorreo, setApCorreo] = useState('');
  const [apDireccion, setApDireccion] = useState('');
  // Investigados (al menos uno).
  const [investigados, setInvestigados] = useState<InvForm[]>([invVacio()]);

  // Fecha efectiva de los hechos (la que se haya diligenciado) y si es tentativa.
  const hFecha = hFechaReal || hFechaTentativa;
  const esTentativaHechos = hFechaReal === '' && hFechaTentativa !== '';

  const [guardando, setGuardando] = useState(false);
  // Cuadrícula abierta en la Etapa 2 (su panel se muestra debajo). null = ninguna.
  const [abierto, setAbierto] = useState<string | null>(null);
  // Etapa 1 = pre-radicación. La Etapa 2 (datos de la notificación disciplinaria) solo se muestra cuando
  // la solicitud está completa. `solicitud` es la SolicitudRadicacion en curso.
  const [solicitud, setSolicitud] = useState<Solicitud | null>(null);
  // Bolita activa del asistente de pre-radicación (0 = Ingreso · 1 = Libro
  // radicador · 2 = Ruta y decisión). Alimenta el encabezado de la tarjeta para
  // que arriba a la izquierda muestre «Etapa 1» o «Etapa 2» según el paso.
  const [pasoPreRadicacion, setPasoPreRadicacion] = useState(0);
  // Rótulo de la etapa actual del asistente (lo emite PreRadicacionForm y cambia
  // conforme se avanza: Pre-radicación → Libro radicador → Enviar a → Reparto/Comité).
  const [rotuloEtapaPre, setRotuloEtapaPre] = useState('Etapa 1 · Pre-radicación');

  // Rol: el Técnico administrativo, su apoyo y el Director de Instrucción (+ admin)
  // CREAN la pre-radicación (Etapa 1); el Abogado y el Tecnólogo de apoyo llenan
  // la Etapa 2 de sus casos asignados.
  const rol = usuarioActual()?.rol ?? '';
  const esAdmin = rol === 'ADMINISTRADOR';
  const esCreador = esAdmin || rol === 'TECNICO_ADMINISTRATIVO'
    || rol === 'APOYO_TECNICO_ADMINISTRATIVO'
    || rol === 'DIRECTOR_INSTRUCCION';
  // Filler = a quien el técnico le ASIGNÓ el caso (abogado contratista o
  // funcionario) + el tecnólogo de apoyo. Lista única en `lib/roles.ts`.
  const esFiller = esFillerEtapa2(rol);
  // Casos asignados (para el abogado/tecnólogo).
  const [casosAsignados, setCasosAsignados] = useState<Solicitud[]>([]);
  const [cargandoCasos, setCargandoCasos] = useState(false);

  async function cargarCasos() {
    setCargandoCasos(true);
    try { setCasosAsignados(await listarAsignadas()); }
    catch { setCasosAsignados([]); }
    finally { setCargandoCasos(false); }
  }

  useEffect(() => {
    getCatalogos().then(setCat).catch(() => setCat(null));
    if (esFiller) cargarCasos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si venimos de "Expedientes no iniciados", carga la solicitud para continuarla.
  useEffect(() => {
    const raw = localStorage.getItem('solicitudPendiente');
    localStorage.removeItem('solicitudPendiente');
    if (!raw) return;
    try {
      const { id } = JSON.parse(raw) as { id: string };
      if (id) obtenerSolicitud(id).then(onSolicitudGuardada).catch(() => {});
    } catch { /* dato corrupto: ignorar */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si venimos a EDITAR la Etapa 2 (caso devuelto), carga el expediente y precarga el form.
  useEffect(() => {
    const id = localStorage.getItem('expedienteEditar');
    localStorage.removeItem('expedienteEditar');
    if (!id) return;
    getExpediente(id)
      .then((exp) => {
        hidratarDesdeExpediente(exp);
        setEdicionExpId(id);
        setEdicionExp(exp);
        setEdicionEstado(exp.decision_inicial?.estado ?? null);
        setModoEdicion(true);
      })
      .catch(() => avisoError('No se pudo cargar el expediente para editar.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // INFORME → «procedencia» = «<entidad> · Cargo: <cargo>» (y al revés al cargar).
  const componerInforme = (entidad: string, cargo: string) =>
    [entidad.trim(), cargo.trim() && `Cargo: ${cargo.trim()}`].filter(Boolean).join(' · ');
  function leerInforme(p: string) {
    const i = p.indexOf('Cargo:');
    if (i < 0) return { entidad: p.trim(), cargo: '' };
    return { entidad: p.slice(0, i).replace(/[·\s]+$/, '').trim(), cargo: p.slice(i + 'Cargo:'.length).trim() };
  }

  // Precarga todos los campos de la Etapa 2 con los datos actuales del expediente.
  function hidratarDesdeExpediente(exp: ExpedienteDetalle) {
    setTipoIngreso(exp.tipo_ingreso || 'DE_OFICIO');
    setFechaQueja(exp.fecha_queja ? exp.fecha_queja.slice(0, 10) : hoy());
    // El campo se llena del radicado manual; si no hay, cae al radicado del sistema.
    setRadicadoManual(exp.radicado_manual || exp.radicado || '');
    setModalidad(exp.modalidad_conducta ?? '');
    setPrioridad(exp.prioridad ?? '');
    const q = exp.quejosos?.[0];
    if (q) {
      setQNombre(q.nombre ?? ''); setQTipoDoc(q.tipo_documento ?? 'CC'); setQNumDoc(q.numero_documento ?? '');
      setQDireccion(q.direccion ?? ''); setQMunicipio(q.municipio ?? ''); setQDepartamento(q.departamento ?? '');
      setQTelefono(q.telefono ?? ''); setQCorreo(q.correo ?? ''); setQProcedencia(q.procedencia ?? '');
      if (exp.tipo_ingreso === 'INFORME' && q.procedencia) {
        const { entidad, cargo } = leerInforme(q.procedencia);
        setQEntidad(entidad); setQCargo(cargo);
      }
    }
    const h = exp.hechos?.[0];
    if (h) {
      const fecha = h.fecha_hechos ? h.fecha_hechos.slice(0, 10) : '';
      if (h.fecha_hechos_tentativa) { setHFechaTentativa(fecha); setHFechaReal(''); }
      else { setHFechaReal(fecha); setHFechaTentativa(''); }
      setHLugar(h.lugar ?? ''); setHDescripcion(h.descripcion ?? '');
      setHDependencia(h.dependencia_involucrada ?? ''); setHObservaciones(h.observaciones ?? '');
    }
    const ap = exp.apoderados?.[0];
    if (ap) {
      setTieneApoderado('SI'); setApNombre(ap.nombre ?? ''); setApDocumento(ap.numero_documento ?? '');
      setApTarjeta(ap.tarjeta_profesional ?? ''); setApCorreo(ap.correo ?? ''); setApDireccion(ap.direccion_notificacion ?? '');
    } else {
      setTieneApoderado('NO');
    }
    if (exp.investigados?.length) {
      setInvestigados(exp.investigados.map((i) => ({
        tipo: i.tipo, nombre: i.nombre ?? '', tipoDocumento: i.tipo_documento ?? 'CC',
        numeroDocumento: i.numero_documento ?? '', cargo: i.cargo ?? '', entidadNombre: i.entidad_nombre ?? '',
        dependencia: i.dependencia ?? '', calidad: i.calidad ?? '',
      })));
    }
  }

  // Cada vez que se guarda/actualiza la solicitud (Etapa 1), sincroniza con la Etapa 2:
  // el radicado y la fecha de la queja (que toma la «Fecha de ingreso» de la pre-radicación).
  function onSolicitudGuardada(s: Solicitud) {
    setSolicitud(s);
    if (s.radicado_completo) setRadicadoManual(s.radicado_completo);
    if (s.fecha_ingreso) setFechaQueja(s.fecha_ingreso.slice(0, 10));
    precargarQuejoso(s);
  }

  /**
   * «¿Quién interpone la notificación disciplinaria?» ya se captura en la Etapa 1:
   * lo diligenciado allá se PRECARGA aquí. Solo se copian los valores que la
   * Etapa 1 traiga (nunca borra lo ya escrito en la Etapa 2) y todo sigue siendo
   * editable: si allá quedó vacío o como ANÓNIMO, aquí se completa/corrige cuando
   * consigan los datos.
   */
  function precargarQuejoso(s: Solicitud) {
    const copiar = (v: string | null | undefined, aplicar: (x: string) => void) => {
      if (v) aplicar(v);
    };
    copiar(s.tipo_ingreso_queja, setTipoIngreso);
    copiar(s.quejoso_nombre, setQNombre);
    copiar(s.quejoso_tipo_documento, setQTipoDoc);
    copiar(s.quejoso_numero_documento, setQNumDoc);
    copiar(s.quejoso_direccion, setQDireccion);
    copiar(s.quejoso_departamento, setQDepartamento);
    copiar(s.quejoso_municipio, setQMunicipio);
    copiar(s.quejoso_telefono, setQTelefono);
    copiar(s.quejoso_correo, setQCorreo);
    copiar(s.quejoso_entidad_origen, setQEntidad);
    copiar(s.quejoso_cargo, setQCargo);
  }

  const tiposIngreso = cat?.tipoIngresoQueja?.length ? cat.tipoIngresoQueja : TIPO_INGRESO_DEF;
  const modalidades = cat?.modalidadConducta?.length ? cat.modalidadConducta : MODALIDAD_DEF;
  const tiposDoc = cat?.tipoDocumento?.length ? cat.tipoDocumento : TIPO_DOC_DEF;
  const tiposImpl = cat?.tipoImplicado?.length ? cat.tipoImplicado : TIPO_IMPL_DEF;
  const calidades = cat?.calidadImplicado?.length ? cat.calidadImplicado : CALIDAD_DEF;

  function setInv(i: number, patch: Partial<InvForm>) {
    setInvestigados((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function agregarInv() { setInvestigados((arr) => [...arr, invVacio()]); }
  function quitarInv(i: number) { setInvestigados((arr) => arr.filter((_, idx) => idx !== i)); }

  // Consulta si el número de documento del investigado i ya tiene expediente.
  // Se dispara al salir del campo (onBlur): una sola petición por edición.
  async function verificarDocumento(i: number, doc: string) {
    const n = doc.trim();
    if (n.length < 4) { setInv(i, { dup: null, dupCargando: false }); return; }
    setInv(i, { dupCargando: true });
    try {
      const r = await buscarExpedientesPorDocumento(n);
      // Guardar solo si el campo no cambió mientras tanto.
      setInvestigados((arr) => arr.map((x, idx) =>
        idx === i && x.numeroDocumento.trim() === n
          ? { ...x, dup: r, dupCargando: false }
          : idx === i ? { ...x, dupCargando: false } : x));
    } catch {
      setInv(i, { dup: null, dupCargando: false });
    }
  }

  function limpiar() {
    setTipoIngreso('DE_OFICIO'); setFechaQueja(hoy()); setRadicadoManual('');
    setModalidad(''); setPrioridad('');
    setQNombre(''); setQTipoDoc('CC'); setQNumDoc(''); setQDireccion('');
    setQMunicipio(''); setQDepartamento(''); setQTelefono(''); setQCorreo('');
    setQProcedencia(''); setQEntidad(''); setQCargo('');
    setHFechaReal(''); setHFechaTentativa(''); setHLugar(''); setHDescripcion(''); setHDependencia(''); setHObservaciones('');
    setTieneApoderado(''); setApNombre(''); setApDocumento(''); setApTarjeta(''); setApCorreo(''); setApDireccion('');
    setInvestigados([invVacio()]);
  }

  // Arma el RadicarInput desde el formulario. Devuelve null (y avisa) si algo falta.
  function armarBody(): RadicarInput | null {
    // Validaciones mínimas de cliente (el backend valida a fondo).
    if (!hDescripcion.trim()) { avisoError('La descripción de los hechos es obligatoria.'); return null; }
    if (investigados.length === 0) { avisoError('Agregue al menos un investigado.'); return null; }
    if (tieneApoderado === 'SI' && !apNombre.trim()) { avisoError('Indique el nombre del apoderado.'); return null; }

    const invs: InvestigadoInput[] = investigados.map((v) => {
      if (v.tipo === 'POR_ESTABLECER') return { tipo: 'POR_ESTABLECER' };
      const inv: InvestigadoInput = { tipo: 'DETERMINADO' };
      if (v.nombre.trim()) inv.nombre = v.nombre.trim();
      if (v.tipoDocumento) inv.tipoDocumento = v.tipoDocumento;
      if (v.numeroDocumento.trim()) inv.numeroDocumento = v.numeroDocumento.trim();
      if (v.cargo.trim()) inv.cargo = v.cargo.trim();
      if (v.entidadNombre.trim()) inv.entidadNombre = v.entidadNombre.trim();
      if (v.dependencia.trim()) inv.dependencia = v.dependencia.trim();
      if (v.calidad.trim()) inv.calidad = v.calidad.trim();
      return inv;
    });

    const body: RadicarInput = {
      tipoIngreso: tipoIngreso as RadicarInput['tipoIngreso'],
      fechaQueja,
      investigados: invs,
      hechos: {
        descripcion: hDescripcion.trim(),
        ...(hFecha ? { fechaHechos: hFecha } : {}),
        ...(esTentativaHechos ? { esTentativa: true } : {}),
        ...(hLugar.trim() ? { lugar: hLugar.trim() } : {}),
        ...(hDependencia.trim() ? { dependenciaInvolucrada: hDependencia.trim() } : {}),
        ...(hObservaciones.trim() ? { observaciones: hObservaciones.trim() } : {}),
      },
    };
    if (radicadoManual.trim()) body.radicadoManual = radicadoManual.trim();
    if (modalidad) body.modalidadConducta = modalidad as RadicarInput['modalidadConducta'];
    // La fecha base de prescripción es la fecha de los hechos; el backend le suma 5 años.
    if (hFecha) body.fechaBasePrescripcion = hFecha;
    if (prioridad.trim()) body.prioridad = prioridad.trim();

    if (tieneApoderado === 'SI' && apNombre.trim()) {
      body.apoderado = {
        nombre: apNombre.trim(),
        ...(apDocumento.trim() ? { numeroDocumento: apDocumento.trim() } : {}),
        ...(apTarjeta.trim() ? { tarjetaProfesional: apTarjeta.trim() } : {}),
        ...(apCorreo.trim() ? { correo: apCorreo.trim() } : {}),
        ...(apDireccion.trim() ? { direccionNotificacion: apDireccion.trim() } : {}),
      };
    }

    if (tipoIngreso === 'ANONIMO') {
      // Anónimo: no se piden datos personales, solo la procedencia.
      if (qProcedencia.trim()) body.quejoso = { procedencia: qProcedencia.trim() };
    } else if (tipoIngreso === 'INFORME') {
      // Informe: entidad que lo remite (con el cargo, en «procedencia»), funcionario, teléfono y correo.
      const q: RadicarInput['quejoso'] = {
        ...(qNombre.trim() ? { nombre: qNombre.trim() } : {}),
        ...(qTelefono.trim() ? { telefono: qTelefono.trim() } : {}),
        ...(qCorreo.trim() ? { correo: qCorreo.trim() } : {}),
        ...(componerInforme(qEntidad, qCargo) ? { procedencia: componerInforme(qEntidad, qCargo) } : {}),
      };
      if (Object.keys(q).length) body.quejoso = q;
    } else if (tipoIngreso === 'QUEJOSO') {
      // Quejoso: datos de la persona. (De oficio no guarda datos de quien interpone.)
      const q: RadicarInput['quejoso'] = {
        ...(qNombre.trim() ? { nombre: qNombre.trim() } : {}),
        ...(qTipoDoc ? { tipoDocumento: qTipoDoc } : {}),
        ...(qNumDoc.trim() ? { numeroDocumento: qNumDoc.trim() } : {}),
        ...(qDireccion.trim() ? { direccion: qDireccion.trim() } : {}),
        ...(qMunicipio.trim() ? { municipio: qMunicipio.trim() } : {}),
        ...(qDepartamento.trim() ? { departamento: qDepartamento.trim() } : {}),
        ...(qTelefono.trim() ? { telefono: qTelefono.trim() } : {}),
        ...(qCorreo.trim() ? { correo: qCorreo.trim() } : {}),
      };
      if (Object.keys(q).length) body.quejoso = q;
    }

    return body;
  }

  // Submit del formulario. En radicación crea el expediente; en edición guarda los
  // cambios y —si `continuar`— reenvía el caso al abogado especialista (avanza el flujo).
  async function procesar(continuar: boolean) {
    const body = armarBody();
    if (!body) return;

    setGuardando(true);
    try {
      // Modo edición: corrige la Etapa 2 de un caso devuelto (no crea expediente nuevo).
      if (modoEdicion && edicionExpId) {
        const id = edicionExpId;
        await editarEtapa2(id, body);
        if (continuar) {
          await reenviarDecision(id); // DEVUELTA_ETAPA2 → PENDIENTE_ESPECIALISTA
          avisoExito('Cambios guardados y expediente reenviado', 'El abogado especialista revisará la corrección.');
        } else {
          avisoExito('Datos de la Etapa 2 actualizados', 'Puede seguir editando o reenviar cuando esté listo.');
        }
        limpiar();
        setModoEdicion(false);
        setEdicionExpId(null);
        setEdicionEstado(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onVolverExpediente?.(id); // vuelve al detalle
        return;
      }
      const exp = await radicar(body);
      // Enlaza la solicitud de pre-radicación con el expediente y la marca radicada.
      if (solicitud) {
        try {
          await radicarSolicitud(solicitud.id, exp.id);
        } catch (e) {
          avisoInfo('Expediente creado, pero no se pudo enlazar la solicitud.', e instanceof Error ? e.message : undefined);
        }
      }
      avisoExito('Notificación disciplinaria radicada correctamente', `Radicado ${exp.radicado} · Continúe con la decisión inicial.`);
      limpiar();
      setSolicitud(null); // vuelve a la Etapa 1 / a la lista de casos asignados
      // El contratista/tecnólogo continúa de forma intuitiva con la Etapa 3: se abre
      // el expediente en la pestaña Instrucción (decisión inicial).
      if (esFiller && onVolverExpediente) {
        onVolverExpediente(exp.id);
        return;
      }
      if (esFiller) cargarCasos(); // refresca los casos pendientes del abogado/tecnólogo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      avisoError(modoEdicion ? 'No se pudieron guardar los cambios' : 'No se pudo radicar la notificación disciplinaria', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  // El submit por defecto (Enter / botón principal): radicar, o guardar sin reenviar en edición.
  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    // Solo guardan los botones propios del formulario («Guardar cambios» / «Radicar»). Los del
    // auto de personería incrustado (Generar, Guardar borrador, Firmar…) también disparan este
    // submit —son botones dentro del <form> o burbujean desde su diálogo— y no deben guardar
    // ni validar la Etapa 2.
    const quien = (e.nativeEvent as SubmitEvent).submitter;
    if (quien && !quien.hasAttribute('data-guardar')) return;
    await procesar(false);
  }

  // ---- Estado de diligenciamiento de cada cuadrícula (para el chulo y el avance) ----
  const invLleno = (v: InvForm) => v.tipo === 'POR_ESTABLECER' || v.nombre.trim() !== '';
  const lleno: Record<string, boolean> = {
    // De oficio no exige datos; los demás, lo mínimo de su tipo.
    origen: tipoIngreso !== '' && (tipoIngreso === 'DE_OFICIO' ? true
      : tipoIngreso === 'ANONIMO' ? qProcedencia.trim() !== ''
        : tipoIngreso === 'INFORME' ? (qEntidad.trim() !== '' || qNombre.trim() !== '') : qNombre.trim() !== ''),
    fechaQueja: fechaQueja !== '',
    radicadoManual: radicadoManual.trim() !== '',
    investigados: investigados.length > 0 && investigados.every(invLleno),
    fechaHechos: hFecha !== '',
    descripcion: hDescripcion.trim() !== '',
    lugar: hLugar.trim() !== '',
    dependencia: hDependencia.trim() !== '',
    observaciones: hObservaciones.trim() !== '',
    // 'NO' = respondido (sin apoderado); 'SI' = respondido con nombre.
    apoderado: tieneApoderado === 'NO' || (tieneApoderado === 'SI' && apNombre.trim() !== ''),
    prescripcion: modalidad !== '' && hFecha !== '',
    prioridad: prioridad.trim() !== '',
  };
  const requeridasOk = CAMPOS.filter((c) => c.requerido).every((c) => lleno[c.id]);
  const diligenciadas = CAMPOS.filter((c) => lleno[c.id]).length;
  const campoSel = abierto ? CAMPOS.find((c) => c.id === abierto) ?? null : null;

  // La Etapa 2 la ve: el admin (flujo completo, cuando la solicitud está completa) y
  // los fillers (abogado/tecnólogo) cuando han seleccionado un caso asignado.
  const mostrarEtapa2 = modoEdicion || (esAdmin && !!solicitud?.completa) || (esFiller && solicitud != null);

  // Formulario que se muestra en el panel de la cuadrícula seleccionada.
  function renderPanel(id: string) {
    switch (id) {
      case 'origen':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>De oficio · Quejoso · Informe · Queja anónima</Label>
              <Select value={tipoIngreso} onValueChange={setTipoIngreso}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tiposIngreso.map((t) => (
                    <SelectItem key={t} value={t}>{labelTipoIngreso(t)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Cada tipo muestra SUS campos (como al iniciar el proceso, Etapa 1):
                De oficio → ninguno · Quejoso → la persona · Informe → entidad y funcionario · Anónima → procedencia. */}
            {/* Cada tipo muestra SU formulario (como al iniciar el proceso, Etapa 1), y todo es
                editable en cualquier momento: De oficio → sin campos · Informe → entidad y
                funcionario · Quejoso → la persona · Anónima → procedencia. */}
            {tipoIngreso === 'DE_OFICIO' && (
              <p className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
                Ingreso de oficio: lo inicia la propia entidad, no se diligencian datos de quien interpone.
              </p>
            )}
            {tipoIngreso === 'INFORME' && (
              <div className="space-y-3 rounded-md border bg-background p-3">
                <div className="flex items-center gap-2 text-sm font-medium"><Users className="size-4" /> Quién remite el informe</div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2"><Label>Secretaría, dependencia o entidad</Label><Input value={qEntidad} onChange={(e) => setQEntidad(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Nombre del funcionario</Label><Input value={qNombre} onChange={(e) => setQNombre(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Cargo</Label><Input value={qCargo} onChange={(e) => setQCargo(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Teléfono</Label><Input value={qTelefono} onChange={(e) => setQTelefono(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Correo electrónico</Label><Input type="email" value={qCorreo} onChange={(e) => setQCorreo(e.target.value)} /></div>
                </div>
              </div>
            )}
            {tipoIngreso === 'QUEJOSO' && (
              <div className="space-y-3 rounded-md border bg-background p-3">
                <div className="flex items-center gap-2 text-sm font-medium"><Users className="size-4" /> Datos de quien interpone la notificación disciplinaria</div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label>Nombre</Label><Input value={qNombre} onChange={(e) => setQNombre(e.target.value)} /></div>
                  <div className="space-y-2">
                    <Label>Tipo de documento</Label>
                    <Select value={qTipoDoc} onValueChange={setQTipoDoc}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>{tiposDoc.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Número de documento</Label><Input inputMode="numeric" value={qNumDoc} onChange={(e) => setQNumDoc(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Teléfono</Label><Input value={qTelefono} onChange={(e) => setQTelefono(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Correo</Label><Input type="email" value={qCorreo} onChange={(e) => setQCorreo(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Dirección</Label><Input value={qDireccion} onChange={(e) => setQDireccion(e.target.value)} /></div>
                  <div className="space-y-2">
                    <Label>Departamento</Label>
                    <Select
                      value={qDepartamento || undefined}
                      onValueChange={(v) => { setQDepartamento(v); setQMunicipio(''); }}
                    >
                      <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                      <SelectContent position="popper">{DEPARTAMENTOS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Municipio</Label>
                    <Select
                      value={qMunicipio || undefined}
                      onValueChange={setQMunicipio}
                      disabled={!qDepartamento}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={qDepartamento ? 'Seleccione…' : 'Elija primero el departamento'} />
                      </SelectTrigger>
                      <SelectContent position="popper">{municipiosDe(qDepartamento).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            {/* Anónimo: sin datos personales, solo de dónde proviene la queja. */}
            {tipoIngreso === 'ANONIMO' && (
              <div className="space-y-2 rounded-md border bg-background p-3">
                <Label htmlFor="qProcedencia">¿De dónde proviene la notificación disciplinaria?</Label>
                <Input
                  id="qProcedencia"
                  value={qProcedencia}
                  onChange={(e) => setQProcedencia(e.target.value)}
                  maxLength={255}
                  placeholder="Ej.: correo anónimo, buzón físico, redes sociales…"
                />
                <p className="text-xs text-muted-foreground">Notificación disciplinaria anónima: no se registran datos personales.</p>
              </div>
            )}
          </div>
        );

      case 'fechaQueja':
        return (
          <div className="space-y-2">
            <Label htmlFor="fechaQueja">Seleccione la fecha de la notificación disciplinaria</Label>
            <Input id="fechaQueja" type="date" value={fechaQueja} onChange={(e) => setFechaQueja(e.target.value)} />
          </div>
        );

      case 'radicadoManual':
        return (
          <div className="space-y-2">
            <Label htmlFor="radicadoManual">Consecutivo del radicado</Label>
            <Input id="radicadoManual" value={radicadoManual} onChange={(e) => setRadicadoManual(e.target.value)} placeholder="Ej.: RAD-2026-0093" />
          </div>
        );

      case 'investigados':
        return (
          <div className="space-y-4">
            {investigados.map((inv, i) => (
              <div key={i} className="rounded-lg border bg-background p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium">Investigado {i + 1}</span>
                  {investigados.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => quitarInv(i)}>
                      <Trash2 className="size-3.5" /> Quitar
                    </Button>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={inv.tipo} onValueChange={(v) => setInv(i, { tipo: v as InvForm['tipo'] })}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {tiposImpl.map((t) => <SelectItem key={t} value={t}>{labelTipoImplicado(t)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {inv.tipo === 'DETERMINADO' && (
                    <>
                      <div className="space-y-2"><Label>Nombre</Label><Input value={inv.nombre} onChange={(e) => setInv(i, { nombre: e.target.value })} /></div>
                      <div className="space-y-2">
                        <Label>Tipo de documento</Label>
                        <Select value={inv.tipoDocumento} onValueChange={(v) => setInv(i, { tipoDocumento: v })}>
                          <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                          <SelectContent>{tiposDoc.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Número de documento</Label>
                        <Input
                          inputMode="numeric"
                          value={inv.numeroDocumento}
                          onChange={(e) => setInv(i, { numeroDocumento: e.target.value, dup: null })}
                          onBlur={(e) => verificarDocumento(i, e.target.value)}
                        />
                        {inv.dupCargando && (
                          <p className="text-xs text-muted-foreground">Verificando documento…</p>
                        )}
                        {inv.dup?.existe && (
                          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                            <div className="flex items-center gap-1.5 font-medium">
                              <AlertTriangle className="size-3.5" />
                              {inv.dup.expedientes.length === 1
                                ? 'Esta persona ya tiene el proceso radicado N.º:'
                                : 'Esta persona ya tiene procesos radicados N.º:'}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {inv.dup.expedientes.map((e) => (
                                <li key={e.expedienteId}>
                                  <span className="font-semibold">{e.radicadoManual || e.radicado}</span>
                                  {e.nombre ? ` — ${e.nombre}` : ''}
                                  <span className="text-amber-700"> ({e.estadoActual.replaceAll('_', ' ').toLowerCase()})</span>
                                </li>
                              ))}
                            </ul>
                            <p className="mt-1 text-amber-700">Verifique si corresponde a la misma persona antes de radicar.</p>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2"><Label>Cargo</Label><Input value={inv.cargo} onChange={(e) => setInv(i, { cargo: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Entidad</Label><Input value={inv.entidadNombre} onChange={(e) => setInv(i, { entidadNombre: e.target.value })} /></div>
                      <div className="space-y-2"><Label>Dependencia</Label><Input value={inv.dependencia} onChange={(e) => setInv(i, { dependencia: e.target.value })} /></div>
                      <div className="space-y-2">
                        <Label>Calidad</Label>
                        {calidades.length ? (
                          <Select value={inv.calidad || NINGUNO} onValueChange={(v) => setInv(i, { calidad: v === NINGUNO ? '' : v })}>
                            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NINGUNO}>— Seleccione —</SelectItem>
                              {calidades.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={inv.calidad} onChange={(e) => setInv(i, { calidad: e.target.value })} />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={agregarInv}>
              <Plus className="size-4" /> Agregar investigado
            </Button>
          </div>
        );

      case 'fechaHechos':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="hReal">Fecha real de los hechos</Label>
                <Input id="hReal" type="date" value={hFechaReal}
                  onChange={(e) => { setHFechaReal(e.target.value); if (e.target.value) setHFechaTentativa(''); }} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hTent">Fecha tentativa (temporal)</Label>
                <Input id="hTent" type="date" value={hFechaTentativa}
                  onChange={(e) => { setHFechaTentativa(e.target.value); if (e.target.value) setHFechaReal(''); }} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Diligencie <strong>solo una</strong>: la real o la tentativa. La prescripción se calcula con la que ingrese.
            </p>
            {esTentativaHechos && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>Fecha <strong>tentativa</strong>: el expediente quedará marcado como pendiente de la fecha real; podrá cambiarla luego en el detalle del expediente.</span>
              </div>
            )}
          </div>
        );

      case 'descripcion':
        return (
          <div className="space-y-2">
            <Label htmlFor="hDescripcion">Describa brevemente los hechos</Label>
            <Textarea id="hDescripcion" rows={4} value={hDescripcion} onChange={(e) => setHDescripcion(e.target.value)} />
          </div>
        );

      case 'lugar':
        return (
          <div className="space-y-2">
            <Label htmlFor="hLugar">Lugar de los hechos</Label>
            <Input id="hLugar" value={hLugar} onChange={(e) => setHLugar(e.target.value)} />
          </div>
        );

      case 'dependencia':
        return (
          <div className="space-y-2">
            <Label htmlFor="hDependencia">Dependencia involucrada</Label>
            <Input id="hDependencia" value={hDependencia} onChange={(e) => setHDependencia(e.target.value)} />
          </div>
        );

      case 'observaciones':
        return (
          <div className="space-y-2">
            <Label htmlFor="hObservaciones">Observaciones</Label>
            <Input id="hObservaciones" value={hObservaciones} onChange={(e) => setHObservaciones(e.target.value)} />
          </div>
        );

      case 'apoderado':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>¿Hay apoderado?</Label>
              <Select value={tieneApoderado || NINGUNO} onValueChange={(v) => setTieneApoderado(v === NINGUNO ? '' : v)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NINGUNO}>— Seleccione —</SelectItem>
                  <SelectItem value="SI">Sí</SelectItem>
                  <SelectItem value="NO">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tieneApoderado === 'SI' && (
              <div className="space-y-3 rounded-md border bg-background p-3">
                <div className="flex items-center gap-2 text-sm font-medium"><UserCheck className="size-4" /> Datos del apoderado</div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Nombre del apoderado <span className="text-destructive">*</span></Label>
                    <Input value={apNombre} onChange={(e) => setApNombre(e.target.value)} />
                  </div>
                  <div className="space-y-2"><Label>Documento de identidad del apoderado</Label><Input inputMode="numeric" value={apDocumento} onChange={(e) => setApDocumento(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Tarjeta profesional del apoderado</Label><Input value={apTarjeta} onChange={(e) => setApTarjeta(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Correo</Label><Input type="email" value={apCorreo} onChange={(e) => setApCorreo(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Dirección de notificación</Label><Input value={apDireccion} onChange={(e) => setApDireccion(e.target.value)} /></div>
                </div>
              </div>
            )}
            {/* Defensa del investigado: la MISMA tarjeta y el MISMO registro que en la investigación
                (y que Sujetos y Otros documentos): defensores, reconocimiento de personería,
                designación de oficio, renuncias con su auto y la trazabilidad de todo. */}
            {edicionExp ? (
              <DefensaCard exp={edicionExp}
                apoderado={tieneApoderado === 'SI' ? { nombre: apNombre, documento: apDocumento, tarjeta: apTarjeta } : undefined} />
            ) : tieneApoderado === 'SI' && (
              <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                Los autos de la defensa (reconocimiento de personería, renuncias…) se generan cuando el expediente quede
                radicado: aquí al corregir la Etapa 2, en la investigación o en «Otros documentos», con estos datos.
              </p>
            )}
          </div>
        );

      case 'prescripcion':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Modalidad de conducta</Label>
                <Select value={modalidad || NINGUNO} onValueChange={(v) => setModalidad(v === NINGUNO ? '' : v)}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NINGUNO}>— Seleccione —</SelectItem>
                    {modalidades.map((m) => <SelectItem key={m} value={m}>{labelModalidad(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Regla configurable de la modalidad elegida (Art. 33). */}
                {reglaPrescripcion(modalidad) && (
                  <p className="text-xs text-primary">{reglaPrescripcion(modalidad)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="presFechaHechos">Fecha de los hechos</Label>
                <Input id="presFechaHechos" type="date" value={hFecha} readOnly disabled />
                {!hFecha && <p className="text-xs text-muted-foreground">Se toma de la opción «Fecha de los hechos».</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="presFechaBase">Fecha base de prescripción</Label>
                <Input id="presFechaBase" type="date" value={masCincoAnios(hFecha)} readOnly disabled />
                <p className="text-xs text-muted-foreground">Automática: fecha de los hechos + 5 años (Art. 33).</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              La prescripción (Art. 33) es de 5 años desde la fecha base: instantánea desde los hechos;
              permanente/continuada desde el último acto; omisiva desde el cese del deber.
            </p>
          </div>
        );

      case 'prioridad':
        return (
          <div className="space-y-2">
            <Label htmlFor="prioridad">Prioridad</Label>
            <Input id="prioridad" value={prioridad} onChange={(e) => setPrioridad(e.target.value)} placeholder="Ej.: ALTA" />
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="space-y-6">
      <HeroVista
        icono={FolderPlus}
        titulo="Control de ingreso"
        descripcion=""
      />

      {/* ---- Etapa 1 · Pre-radicación: SOLO el creador (técnico administrativo / admin) ---- */}
      {esCreador && !modoEdicion && (
        <Card>
          <CardHeader>
            {/* El rótulo de arriba a la izquierda sigue a la bolita activa y cambia
                conforme se avanza: Pre-radicación → Libro radicador → Enviar a →
                Reparto/Comité (lo emite PreRadicacionForm vía onPasoChange). */}
            <CardTitle>{rotuloEtapaPre}</CardTitle>
          </CardHeader>
          <CardContent>
            <PreRadicacionForm
              solicitud={solicitud}
              onGuardada={onSolicitudGuardada}
              onPasoChange={(paso, rotulo) => { setPasoPreRadicacion(paso); setRotuloEtapaPre(rotulo); }}
              onSalir={() => { limpiar(); setSolicitud(null); }}
            />
          </CardContent>
        </Card>
      )}

      {/* ---- Filler (abogado / tecnólogo de apoyo): lista de casos asignados ---- */}
      {esFiller && !solicitud && !modoEdicion && (
        <Card>
          <CardHeader><CardTitle>Expedientes asignados a mí</CardTitle></CardHeader>
          <CardContent>
            {cargandoCasos ? (
              <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : casosAsignados.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tienes expedientes pendientes de diligenciar.</p>
            ) : (
              <ul className="divide-y">
                {casosAsignados.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="font-medium">{c.radicado_completo ?? c.radicado_origen ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">Ingreso: {fmtFecha(c.fecha_ingreso)} · {labelCanal(c.canal)}</p>
                      {c.asignado_por_nombre && (
                        <p className="text-xs text-muted-foreground">Asignado por: {c.asignado_por_nombre}</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => onSolicitudGuardada(c)}>Diligenciar Etapa 2</Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Filler con caso seleccionado: barra para volver a la lista ---- */}
      {esFiller && solicitud && (
        <div className="flex items-center justify-between rounded-md border bg-muted/30 px-4 py-2 text-sm">
          <span>Expediente: <strong>{solicitud.radicado_completo ?? solicitud.radicado_origen}</strong></span>
          <Button size="sm" variant="ghost" onClick={() => { limpiar(); setSolicitud(null); }}>Volver a mis expedientes</Button>
        </div>
      )}

      {/* Etapa 2 · datos de la notificación disciplinaria (según el rol). */}
      {mostrarEtapa2 ? (
      <form onSubmit={enviar} className="space-y-6">
        {modoEdicion ? (
          <div className="flex items-center justify-between rounded-md border border-amber-400/50 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span>
              {edicionEstado === 'DEVUELTA_ETAPA2' ? (
                <>
                  <strong>Corrigiendo Etapa 2</strong> — el expediente fue devuelto a corregir. Edite lo necesario y use
                  <strong> «Guardar y reenviar»</strong> para devolverlo al abogado especializado, o <strong>«Guardar cambios»</strong> si aún no quiere reenviarlo.
                </>
              ) : (
                <>
                  <strong>Editando Etapa 2</strong> — ajuste los datos de la notificación disciplinaria y <strong>«Guardar cambios»</strong>.
                  La decisión se elige/propone desde la pestaña <strong>Decisión inicial</strong>.
                </>
              )}
            </span>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setModoEdicion(false); setEdicionExpId(null); setEdicionEstado(null); limpiar(); onVolverExpediente?.(edicionExpId ?? ''); }}>
              Cancelar
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
            Etapa 2 · Dé clic en cada cuadrícula para diligenciar los <strong>datos de la notificación disciplinaria</strong>.
            Al radicar, el expediente quedará enlazado a la solicitud <strong>{solicitud?.radicado_completo}</strong>.
          </div>
        )}

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle>Expediente · datos de la notificación disciplinaria</CardTitle>
            <span className={cn(
              'rounded-full px-2.5 py-0.5 text-xs font-medium',
              requeridasOk ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
            )}>
              {diligenciadas} de {CAMPOS.length} diligenciadas
            </span>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Cuadrículas seleccionables: una por opción del expediente */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CAMPOS.map((c) => {
                const sel = abierto === c.id;
                const done = lleno[c.id];
                const Ico = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setAbierto(sel ? null : c.id)}
                    className={cn(
                      'relative flex items-start gap-3 rounded-lg border p-3 text-left transition',
                      sel ? 'border-primary ring-2 ring-primary/30' : 'hover:border-primary/50',
                      done ? 'bg-primary/5' : 'bg-card',
                    )}
                  >
                    <span className={cn(
                      'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md',
                      done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}>
                      <Ico className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1 pr-4">
                      <span className="flex items-center gap-1 text-sm font-medium leading-tight">
                        {c.label}{c.requerido && <span className="text-destructive">*</span>}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {done ? 'Diligenciado' : 'Pendiente por diligenciar'}
                      </span>
                    </span>
                    {done && (
                      <span className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Barra de avance del diligenciamiento */}
            <div className="space-y-1">
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(diligenciadas / CAMPOS.length) * 100}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">
                {requeridasOk ? '¡Campos obligatorios diligenciados!' : `${diligenciadas} de ${CAMPOS.length} diligenciadas`}
              </p>
            </div>

            {/* Panel de la cuadrícula seleccionada */}
            {campoSel && (() => {
              const IconSel = campoSel.icon;
              return (
                <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <IconSel className="size-4 text-primary" /> {campoSel.label}
                    {campoSel.requerido && <span className="text-destructive">*</span>}
                  </div>
                  {campoSel.ayuda && <p className="text-xs text-muted-foreground">{campoSel.ayuda}</p>}
                  {renderPanel(campoSel.id)}
                  <div className="flex justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAbierto(null)}>Cerrar</Button>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="mr-auto text-sm text-muted-foreground">{diligenciadas} de {CAMPOS.length} opciones diligenciadas</span>
          {modoEdicion ? (
            <>
              {/* Guardar los cambios de la Etapa 2 */}
              <Button type="submit" data-guardar variant={edicionEstado === 'DEVUELTA_ETAPA2' ? 'outline' : 'default'} disabled={guardando || !requeridasOk} title="Guardar los cambios de la Etapa 2">
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </Button>
              {/* Guardar y reenviar al abogado especializado: solo si el caso fue devuelto */}
              {edicionEstado === 'DEVUELTA_ETAPA2' && (
                <Button type="button" disabled={guardando || !requeridasOk} onClick={() => procesar(true)} title={requeridasOk ? 'Guardar y reenviar al abogado especializado' : 'Diligencie las cuadrículas obligatorias (*)'}>
                  {guardando ? 'Enviando…' : 'Guardar y reenviar'}
                </Button>
              )}
            </>
          ) : (
            <Button type="submit" data-guardar disabled={guardando || !requeridasOk} title={requeridasOk ? 'Radicar la notificación disciplinaria' : 'Diligencie las cuadrículas obligatorias (*)'}>
              {guardando ? 'Radicando…' : 'Radicar notificación disciplinaria'}
            </Button>
          )}
        </div>
      </form>
      ) : esCreador && !esAdmin ? (
        // Técnico administrativo: no llena la Etapa 2; su trabajo termina en la pre-radicación.
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {solicitud?.completa ? (
              <>Pre-radicación <strong>completa</strong>. El expediente quedó asignado al <strong>abogado</strong> y al
              <strong> tecnólogo de apoyo</strong>, que diligenciarán la Etapa 2. Tu parte termina aquí.</>
            ) : (
              <>Complete la <strong>Etapa 1 · Pre-radicación</strong> (comité PROCEDE/SE OMITE y abogado
              asignado). Al completarla, el expediente pasa al abogado/tecnólogo de apoyo. Si no la termina,
              queda en «Expedientes no iniciados».</>
            )}
          </CardContent>
        </Card>
      ) : esAdmin ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Complete la <strong>Etapa 1 · Pre-radicación</strong> y guárdela. Cuando el comité
            PROCEDE o SE OMITE y esté asignado el abogado, aquí se habilitarán los <strong>datos
            de la notificación disciplinaria</strong>.
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
